import { ArrowRight } from 'lucide-react';

const footerLinks = {
  Services: [
  { label: 'AI & Data', href: '/services/ai-data' },
  { label: 'Cybersecurity', href: '/cybersecurity' },
  { label: 'Application Development', href: '/services/application-development' },
  { label: 'Cloud & Infrastructure', href: '/services/cloud' },
  { label: 'Modernization & Automation', href: '/services/modernization-automation' },
  { label: 'Technology Assessment', href: '/assessment' },
  ],
  Company: [
  { label: 'About', href: '/about' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Insights', href: '/insights' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
  ],
  Resources: [
  { label: 'Blog', href: '/insights' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Engagement Models', href: '/engagement-models' },
  { label: 'Partners', href: '/partners' },
  { label: 'Industries', href: '/industries' },
  ],
};

const socialLinks = [
  { icon: 'fa-brands fa-linkedin-in', href: 'https://www.linkedin.com/company/flynaut/', label: 'LinkedIn' },
  { icon: 'fa-brands fa-x-twitter', href: 'https://x.com/flynaut', label: 'X' },
  { icon: 'fa-brands fa-facebook-f', href: '#', label: 'Facebook' },
  { icon: 'fa-brands fa-instagram', href: '#', label: 'Instagram' },
];

const partnerLogos = [
  { name: 'CCO Cornerstone', img: 'https://cdn.flynaut.com/images/CCO-Logo_Primary_Horiz_Reverse_4C.png' },
  { name: 'Premier Staffing Solution', img: 'https://cdn.flynaut.com/images/PSS_Logo_Primary_Reverse.png' },
  { name: 'OneProtect', img: 'https://cdn.flynaut.com/images/OneProtect-FullWhite.png' },
];

export default function Footer() {
  return (
  <footer data-testid="footer" className="bg-[#1A1F2E] pt-16 pb-6" role="contentinfo">
  <div className="max-w-content mx-auto px-6">
  {/* Main grid */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
  {/* Brand Column - spans 2 on lg */}
  <div className="lg:col-span-2 lg:pr-8">
  <img
  src="https://cdn.flynaut.com/images/Logo_for_dark_bg.png"
  alt="Flynaut"
  width="160"
  height="56"
  className="h-14 mb-5"
  />
  <p className="text-white/90 text-base font-medium mb-2">Transforming Tomorrow. Together.</p>
  <p className="text-[#8B95A5] text-sm leading-relaxed mb-6 max-w-xs">
  We build, protect, and modernize the technology that drives business forward. 20+ years, one partner.
  </p>
  <a
  href="/assessment"
  data-testid="footer-book-call"
  className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-150"
  >
  Book a Call <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
  </div>

  {/* Services Column */}
  <div>
  <h4 className="text-white text-sm font-semibold mb-5">Services</h4>
  <ul className="flex flex-col gap-2.5">
  {footerLinks.Services.map((link) => (
  <li key={link.label}>
  <a href={link.href} data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-[#8B95A5] hover:text-white text-sm transition-colors duration-150">
  {link.label}
  </a>
  </li>
  ))}
  </ul>
  </div>

  {/* Company Column */}
  <div>
  <h4 className="text-white text-sm font-semibold mb-5">Company</h4>
  <ul className="flex flex-col gap-2.5">
  {footerLinks.Company.map((link) => (
  <li key={link.label}>
  <a href={link.href} data-testid={`footer-company-${link.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-[#8B95A5] hover:text-white text-sm transition-colors duration-150">
  {link.label}
  </a>
  </li>
  ))}
  </ul>
  </div>

  {/* Resources Column */}
  <div>
  <h4 className="text-white text-sm font-semibold mb-5">Resources</h4>
  <ul className="flex flex-col gap-2.5">
  {footerLinks.Resources.map((link) => (
  <li key={link.label}>
  <a href={link.href} data-testid={`footer-resource-${link.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-[#8B95A5] hover:text-white text-sm transition-colors duration-150">
  {link.label}
  </a>
  </li>
  ))}
  </ul>
  </div>
  </div>

  {/* Our Partners row */}
  <div className="border-t border-white/10 pt-8 mb-8">
  <div className="flex flex-col sm:flex-row items-center gap-6">
  <span className="text-[#8B95A5] text-xs font-semibold uppercase tracking-wider flex-shrink-0">Our Partners</span>
  <div className="flex items-center gap-8">
  {partnerLogos.map((p) => (
  <div key={p.name} data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, '-')}`} className="h-12">
  <img src={p.img} alt={p.name} className="h-full w-auto max-w-[180px] object-contain" loading="lazy" />
  </div>
  ))}
  </div>
  </div>
  </div>

  {/* Social + Bottom bar */}
  <div className="border-t border-white/10 pt-6">
  <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
  {/* Social icons */}
  <div className="flex items-center gap-3">
  {socialLinks.map((s) => (
  <a
  key={s.label}
  href={s.href}
  target="_blank"
  rel="noopener noreferrer"
  data-testid={`social-${s.label.toLowerCase()}`}
  aria-label={s.label}
  className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-[#8B95A5] hover:text-white hover:bg-white/10 transition-all duration-150"
  >
  <i className={`${s.icon} text-xs`} />
  </a>
  ))}
  </div>

  {/* Legal links */}
  <div className="flex flex-wrap items-center gap-4 text-[#6B7280] text-xs">
  <a href="/privacy" className="hover:text-white transition-colors duration-150">Privacy Policy</a>
  <span className="text-white/20">|</span>
  <a href="/terms" className="hover:text-white transition-colors duration-150">Terms of Service</a>
  <span className="text-white/20">|</span>
  <a href="/cookies" className="hover:text-white transition-colors duration-150">Cookie Policy</a>
  <span className="text-white/20">|</span>
  <a href="/accessibility" className="hover:text-white transition-colors duration-150">Accessibility</a>
  </div>

  {/* Copyright */}
  <p className="text-[#6B7280] text-xs">
  Flynaut LLC 2004-{new Date().getFullYear()}
  </p>
  </div>
  </div>
  </div>
  </footer>
  );
}
